/**
 * Admin Config Defaults & Registry
 *
 * Single source of truth for known config keys, their display metadata, and
 * the default value used both as a runtime fallback (when the Dataverse row
 * doesn't exist) and as the seed value when an admin clicks "Initialize" in
 * the AdminConfigPage to create the row.
 *
 * Adding a new config:
 *   1. Add the key + payload type to ConfigKeyMap (types.ts)
 *   2. Add an entry here with category / displayName / description / default
 *   3. (Optional) write a specialized editor and register it in
 *      catalog/admin-config/AdminConfigPage.tsx EDITORS
 */

import type {
  ConfigKey,
  ConfigKeyMap,
  SalesStagesValue,
  SolutionCategoriesValue,
  SalesYearValue,
  RepTeamsValue,
  OutreachTemplateValue,
  PointsMatrixValue,
} from './types';

interface ConfigDefault<K extends ConfigKey> {
  key: K;
  category: string;
  displayName: string;
  description: string;
  value: ConfigKeyMap[K];
}

const SALES_STAGES_DEFAULT: SalesStagesValue = {
  stages: [
    { name: 'Working', isCase: false, isPipeline: true, color: null, order: 0, bpfStageId: '5a47c4aa-5647-4bfd-afd2-6f0bcb12a794' },
    { name: 'Qualify', isCase: false, isPipeline: true, color: null, order: 1, bpfStageId: 'd3ded8f3-2e7b-475a-8c9b-be96bacb8cf9' },
    { name: 'Solution Scoping', isCase: false, isPipeline: true, color: '#2196f3', order: 2, bpfStageId: 'c251728c-ff71-4919-9490-a4999bc3c206' },
    { name: 'Proposal Creation For Open Case', isCase: true, isPipeline: false, color: '#ff9800', order: 3, bpfStageId: 'c248197f-0cf7-4851-85d3-a605a165bf46' },
    { name: 'Presentation', isCase: true, isPipeline: false, color: '#4caf50', order: 4, bpfStageId: '2d945b11-4433-4e44-bda9-633b7ec41ba7' },
    { name: 'Finalization', isCase: false, isPipeline: false, color: null, order: 5, mappedTo: 'Presentation' },
  ],
};

const SOLUTION_CATEGORIES_DEFAULT: SolutionCategoriesValue = {
  categories: [
    {
      name: 'Health Plan Solutions',
      fields: ['dts_tpaadmin', 'dts_veracityrisk', 'dts_providersofexcellence'],
      order: 0,
      color: '#553746',
    },
    {
      name: 'VeracityRx',
      fields: ['dts_veracityrx', 'dts_minimumessentialcoveragemec', 'dts_maplebridge'],
      order: 1,
      color: '#D6461D',
    },
    {
      name: 'Health Centers',
      fields: ['dts_veracityonsite'],
      order: 2,
      color: '#2196f3',
    },
    {
      name: 'Care Solutions',
      fields: [],
      order: 3,
      color: '#7b1fa2',
    },
  ],
  fieldLabels: {
    dts_tpaadmin: 'TPA Admin',
    dts_veracityrx: 'Veracity Rx',
    dts_veracityonsite: 'Veracity OnSite',
    dts_veracityrisk: 'Veracity Risk',
    dts_providersofexcellence: 'Providers of Excellence',
    dts_minimumessentialcoveragemec: 'MEC',
    dts_maplebridge: 'Maplebridge',
  },
};

const SALES_YEAR_DEFAULT: SalesYearValue = { startMonth: 2 };

const REP_TEAMS_DEFAULT: RepTeamsValue = { teams: [] };

const OUTREACH_TEMPLATE_DEFAULT: OutreachTemplateValue = { templates: {} };

const POINTS_MATRIX_DEFAULT: PointsMatrixValue = {
  unknownBrokerDefault: 'prospect',
  schedules: [
    {
      id: 'default',
      startDate: '2026-01-01',
      endDate: null,
      weights: {
        email: { existing: 0.25, prospect: 0.5 },
        phonecall: { existing: 0.5, prospect: 1 },
        appointmentVirtual: { existing: 1, prospect: 2 },
        appointmentFaceToFace: { existing: 3, prospect: 5 },
        appointmentMeal: { existing: 3, prospect: 5 },
        proposalCreated: { existing: 3, prospect: 5 },
        proposalPresented: { existing: 5, prospect: 10 },
      },
    },
  ],
};

export const CONFIG_DEFAULTS: { [K in ConfigKey]: ConfigDefault<K> } = {
  'sales.stages': {
    key: 'sales.stages',
    category: 'Sales',
    displayName: 'Pipeline Stages',
    description: 'Sales pipeline stage definitions — controls which stages are classified as Early Pipeline or Proposal Pipeline (Cases)',
    value: SALES_STAGES_DEFAULT,
  },
  'sales.solutionCategories': {
    key: 'sales.solutionCategories',
    category: 'Sales',
    displayName: 'Sales Solution Categories',
    description: 'Solution-category names, sort order, and colors. Pills throughout Vera Sales (Pipeline Open Cases, Won Cases, Broker Scorecard) inherit color from the product’s category.',
    value: SOLUTION_CATEGORIES_DEFAULT,
  },
  'sales.salesYear': {
    key: 'sales.salesYear',
    category: 'Sales',
    displayName: 'Sales Year',
    description: 'Start month for the sales year (YTD calculations).',
    value: SALES_YEAR_DEFAULT,
  },
  'sales.repTeams': {
    key: 'sales.repTeams',
    category: 'Sales',
    displayName: 'Rep Teams',
    description: 'Groups multiple salespeople under a single reporting name',
    value: REP_TEAMS_DEFAULT,
  },
  'sales.outreachTemplate': {
    key: 'sales.outreachTemplate',
    category: 'Sales',
    displayName: 'Outreach Email Template',
    description: 'Email template used by One Card outreach button',
    value: OUTREACH_TEMPLATE_DEFAULT,
  },
  'sales.pointsMatrix': {
    key: 'sales.pointsMatrix',
    category: 'Sales',
    displayName: 'Activity Points Matrix',
    description: 'Time-gated point weights per activity type, split by Existing vs Prospect broker. Drives the Points view on the Activity Report.',
    value: POINTS_MATRIX_DEFAULT,
  },
};

export const CONFIG_KEYS = Object.keys(CONFIG_DEFAULTS) as ConfigKey[];

export function getConfigDefault<K extends ConfigKey>(key: K): ConfigDefault<K> {
  return CONFIG_DEFAULTS[key];
}
